import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let loader;

const loadScript = () => {
  if (window.turnstile) return Promise.resolve();
  if (!loader) {
    loader = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = SCRIPT; s.async = true; s.defer = true;
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return loader;
};

let enforcedPromise;
// True only when the backend actually checks tokens (TURNSTILE_SECRET set) — never block sign-in otherwise.
export const useTurnstileEnforced = () => {
  const [enforced, setEnforced] = React.useState(false);
  useEffect(() => {
    if (!SITE_KEY) return;
    enforcedPromise = enforcedPromise || fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/turnstile-config`).then((r) => r.json()).catch(() => ({ enforced: false }));
    enforcedPromise.then((d) => setEnforced(!!d.enforced));
  }, []);
  return enforced;
};

// Explicitly rendered widget; tokens are single-use so callers reset() after every submit attempt.
export const Turnstile = forwardRef(({ action, onToken, testId }, ref) => {
  const box = useRef(null);
  const widgetId = useRef(null);
  useImperativeHandle(ref, () => ({
    reset: () => { onToken(""); if (window.turnstile && widgetId.current != null) window.turnstile.reset(widgetId.current); },
  }));
  useEffect(() => {
    if (!SITE_KEY) return undefined;
    let cancelled = false;
    loadScript().then(() => {
      if (cancelled || !box.current) return;
      widgetId.current = window.turnstile.render(box.current, {
        sitekey: SITE_KEY, action, appearance: "always",
        callback: (t) => onToken(t), "expired-callback": () => onToken(""), "error-callback": () => onToken(""),
      });
    }).catch(() => onToken(""));
    return () => {
      cancelled = true;
      if (window.turnstile && widgetId.current != null) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [action]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!SITE_KEY) return null;
  return <div ref={box} className="min-h-[65px]" data-testid={testId} />;
});
Turnstile.displayName = "Turnstile";
